import React, {FC} from 'react'
import {Clear} from '@css/common.elements'
import {Button} from '@components/Button'
import {TestimonialsSection} from '../TestimonialsSection'
import {ImageBlock} from './ImageBlock'
import {PopupHandler} from './PopupHandler'
import {LinkBlock} from './LinkBlock'

import {
  InfoBlockBottomText,
  InfoBlockButtonWrapper,
  InfoBlockContainer,
  InfoBlockContent,
  InfoBlockContentWrapper,
  InfoBlockDisclaimerText,
  InfoBlockHeader,
  InfoBlockHeaderContent,
  InfoBlockHeaderTitle,
  InfoBlockMainContent,
  InfoBlockMainSection,
  InfoBlockSecondarySection,
  InfoBlockSubtitle,
  InfoBlockText,
  InfoBlockTextWrapper
} from './InfoBlock.elements'
import {InfoBlockCheckList} from '@pages/Homepage/components/InfoBlockCheckList'
import {InfoBlockCallNowText} from '@pages/Homepage/components/InfoBlockCallNowText'
import {ProductCardProps} from '@pages/Homepage/components/ProductCardSection'
import Analytics from "@lib/Analytics";
import {LeadgenPopupType} from "@components/LeadgenPopupFlow";

export type InfoBlockProps = {
  type: string
  align?: string
  title: string
  text: string
  link?: string
  linkAction?: 'MiniPopup'
  linkPopup?: {
    title: string
    text: string
  }
  imageUrl: string
  imageUrlMobile: string
  imageHeight: string
  imageHeightMobile: string
  imageMarginBottomMobile: string
  imageAction?: 'MiniPopup'
  imagePopup?: {
    title: string
    text: string
  }
  component?: string
  componentData?: any
  buttonText?: string
  buttonAction?: string
  buttonMinWidth?: boolean
  onButtonClick?: (buttonAction: string | undefined) => void
  onClickLink?: () => void
  mainSectionMaxWidth?: string
  mainSectionPaddingTopDesktop?: string
  paddingTop?: string
  paddingTopMobile?: string
  paddingBottom?: string
  paddingBottomMobile?: string
  backgroundColor?: string
  textColor?: string
  disclaimerText?: string
  topText: string
  bottomText: string
  phoneMapping: PhoneMapping
  productCards: ProductCardProps
  isFeatured: boolean
  mobileReverse?: boolean
  mainSectionMobileWhiteBackground?: boolean
  mobileMainSectionPadding?: boolean
  subtitle?: string
  buttonTextCenter?: boolean
  onBottomTextClick?: (infoBlockActionType: LeadgenPopupType) => void
  infoBlockActionType?: LeadgenPopupType
}

export const InfoBlock: FC<InfoBlockProps> = ({
  align,
  backgroundColor,
  buttonText,
  component,
  componentData,
  disclaimerText,
  link,
  linkPopup,
  linkAction,
  mainSectionMaxWidth,
  mainSectionPaddingTopDesktop,
  onButtonClick,
  buttonAction,
  onClickLink,
  paddingTop,
  paddingTopMobile,
  paddingBottom,
  paddingBottomMobile,
  text,
  textColor,
  title,
  phoneMapping,
  imageUrl,
  imageUrlMobile,
  imageHeight,
  imageHeightMobile,
  imageMarginBottomMobile,
  imageAction,
  imagePopup,
  mobileReverse,
  mainSectionMobileWhiteBackground,
  mobileMainSectionPadding,
  subtitle,
  buttonTextCenter,
  bottomText,
  buttonMinWidth,
  onBottomTextClick,
  infoBlockActionType
}) => {

  const handleButtonClick = () => {
    return onButtonClick && onButtonClick(buttonAction);
  }

  const handleBottomTextClick = () => {
    //for now it opens popup leads
    return onBottomTextClick && onBottomTextClick(infoBlockActionType || LeadgenPopupType.AUTO_INSURANCE);
  }

  const renderCustomComponent = (component: string, componentData: any, textColor?: string) => {
    switch (component) {
      case 'TestimonialSlider':
        return (
          <TestimonialsSection testimonials={componentData} />
        )
      case 'InfoBlockChecklist':
        return componentData.map((section, i) => {
          return (
            <InfoBlockCheckList
              key={`info-block-check-list-item-${i}`}
              text={section.text}
              index={i}
              title={section.title}
              textColor={textColor}
            />
          )
        })
      case 'InfoBlockCallNowText':
        return (
          <InfoBlockCallNowText
            phoneMapping={phoneMapping}
            hours={componentData.hours}
            text={componentData.text}
            justText={componentData.justText}
          />
        )
    }
  }

  return (
    <InfoBlockContainer
      backgroundColor={backgroundColor}>
      <InfoBlockContentWrapper
        paddingTop={paddingTop}
        paddingTopMobile={paddingTopMobile}
        paddingBottom={paddingBottom}
        paddingBottomMobile={paddingBottomMobile}>
        <InfoBlockContent mobileReverse={mobileReverse}>
          <InfoBlockMainSection
            align={align}
            mainSectionPaddingTopDesktop={mainSectionPaddingTopDesktop}
            mainSectionMobileWhiteBackground={mainSectionMobileWhiteBackground}
            mobileMainSectionPadding={mobileMainSectionPadding}
          >
            <InfoBlockMainContent
              align={align}
              mainSectionMaxWidth={mainSectionMaxWidth}>
              <InfoBlockHeader
                backgroundColor={backgroundColor}>
                <InfoBlockHeaderContent>
                  <InfoBlockHeaderTitle
                    textColor={textColor}
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                </InfoBlockHeaderContent>
              </InfoBlockHeader>
              <InfoBlockTextWrapper>
                { subtitle &&
                  <InfoBlockSubtitle dangerouslySetInnerHTML={{ __html: subtitle }} />
                }
                <InfoBlockText
                  textColor={textColor}
                  dangerouslySetInnerHTML={{ __html: text }} />
              </InfoBlockTextWrapper>
              { component && renderCustomComponent(component, componentData, textColor) }
              { buttonText &&
                <InfoBlockButtonWrapper buttonMinWidth={buttonMinWidth} buttonTextCenter={buttonTextCenter}>
                  <Button onClick={handleButtonClick} variant={'green-wide'}>{buttonText}</Button>
                </InfoBlockButtonWrapper>
              }
              { link && (
                <PopupHandler
                  title={linkPopup?.title || ''}
                  text={linkPopup?.text || ''}
                  action={linkAction}
                >
                  <LinkBlock
                    textColor={textColor}
                    link={link}
                    onClick={onClickLink}
                  />
                </PopupHandler>
              )}
              { bottomText &&
                <InfoBlockTextWrapper>
                  <InfoBlockBottomText onClick={handleBottomTextClick} dangerouslySetInnerHTML={{ __html: bottomText }}/>
                </InfoBlockTextWrapper>
              }
            </InfoBlockMainContent>
            <Clear />
          </InfoBlockMainSection>
          <InfoBlockSecondarySection>
            <PopupHandler
              title={imagePopup?.title || ''}
              text={imagePopup?.text || ''}
              action={imageAction}
            >
              <ImageBlock
                imageHeight={imageHeight}
                imageHeightMobile={imageHeightMobile}
                imageMarginBottomMobile={imageMarginBottomMobile}
                imageUrl={imageUrl}
                imageUrlMobile={imageUrlMobile}
              />
            </PopupHandler>
          </InfoBlockSecondarySection>
          <Clear />
        </InfoBlockContent>
      </InfoBlockContentWrapper>
      { disclaimerText &&
        <InfoBlockDisclaimerText
          textColor={textColor}
          dangerouslySetInnerHTML={{ __html: disclaimerText }} />
      }
    </InfoBlockContainer>
  )
}
